import { useEffect } from 'react';
import { atom } from 'jotai';
import { useSetAtom } from 'jotai';
import {
  collectionGroup,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  where,
} from 'firebase/firestore';

import { IPost } from '_interfaces/post.interface';
import { IPostStatus } from '_interfaces/types/post.types';
import { auth, db, refreshFirebaseToken } from 'utils/firebase';
import { localActivityFeedDraftPostsAtom } from './post';
import { selectedProjectIdAtom } from 'atoms/projects';

const editableDraftPostBase = atom<{ draft: IPost | null }>({ draft: null });
export const editableDraftPostAtom = atom(
  (get) => get(editableDraftPostBase).draft,
  (_get, set, value: IPost | null) => set(editableDraftPostBase, { draft: value })
);

export const allUserDraftsAtom = atom<IPost[]>([]);

export const currentProjectDraftsAtom = atom<IPost[]>((get) => {
  const drafts = get(allUserDraftsAtom);
  const projectId = get(selectedProjectIdAtom);
  if (!projectId) return [];
  return drafts.filter((d) => {
    if (Number(d.projectId) !== projectId) return false;
    const hasPendingMedia = d.media?.some((m) => m.url === '');
    return !hasPendingMedia;
  });
});

export function useDraftsListener(companyId: string | number | null, userId: string | null) {
  const setDrafts = useSetAtom(allUserDraftsAtom);

  useEffect(() => {
    if (!companyId || !userId) return;

    const draftsQuery = query(
      collectionGroup(db, 'activityFeed'),
      where('status', '==', IPostStatus.DRAFT),
      where('createdBy.userId', '==', userId),
      where('companyId', '==', Number(companyId)),
      where('isDeleted', '==', false),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      draftsQuery,
      (snapshot) => {
        const drafts: IPost[] = snapshot.docs.map((docSnap) => {
          const data = docSnap.data();
          const parentPath = docSnap.ref.path.replace(`/${docSnap.id}`, '');
          return IPost.fromDocumentData({ ...data, id: docSnap.id, parentPath });
        });
        setDrafts(drafts);
      },
      () => {
        // Firestore listener error — will retry on next snapshot
      }
    );

    return () => unsubscribe();
  }, [companyId, userId, setDrafts]);
}

export const deleteDraftPostAtom = atom(
  null,
  async (get, set, params: { postId: string; parentPath: string }) => {
    const previousDrafts = get(allUserDraftsAtom);
    const previousLocalDrafts = get(localActivityFeedDraftPostsAtom);

    // Optimistic removal
    set(allUserDraftsAtom, (prev) => prev.filter((d) => d.id !== params.postId));
    set(localActivityFeedDraftPostsAtom, (drafts) => drafts.filter((d) => d.id !== params.postId));

    try {
      let firebaseUser = auth.currentUser;
      if (!firebaseUser) {
        const refreshSuccess = await refreshFirebaseToken();
        if (!refreshSuccess) {
          throw new Error('Failed to authenticate with Firebase');
        }
        firebaseUser = auth.currentUser;
      }

      const docRef = doc(db, `${params.parentPath}/${params.postId}`);
      await deleteDoc(docRef);
    } catch (error) {
      // Rollback optimistic state so draft reappears in UI
      set(allUserDraftsAtom, previousDrafts);
      set(localActivityFeedDraftPostsAtom, previousLocalDrafts);
      throw error;
    }
  }
);
